import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Handles the return leg of Emergent Google OAuth: exchanges the one-time session_id
// (in the URL fragment) for our app session, then routes the user to their home by role.
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");
    // Clear the fragment so the token never lingers in the URL/history.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!sessionId) { navigate("/login", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        refreshUser(data.user);
        const role = data.user?.role;
        const dest = role === "subscriber" ? "/dashboard" : role === "admin" ? "/admin" : "/home";
        navigate(dest, { replace: true });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
        <p className="text-muted-foreground mt-4">Signing you in…</p>
      </div>
    </div>
  );
}
